import React, {
  type ReactElement,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { Button } from 'rebass';
import Icon from '../Icon/Icon';
import useNotify from '../../hooks/useNotify';

const COPIED_DURATION = 3000;

type CopyButtonProps = {
  /** The text to copy to clipboard. */
  readonly text: string;
  /**
   * Human-readable name for the thing being copied.
   * Used to build the button's `aria-label` (`"Copy <label>"`).
   */
  readonly label?: string;
};

/**
 * An icon button that copies the `textContent` of the element identified by
 * the `for` prop to the clipboard when clicked.
 * Shows a checkmark icon after a successful copy, then reverts to the copy icon.
 * Shows an error notification if the element is not found or the copy fails.
 */
export function CopyButton({ text, label }: CopyButtonProps): ReactElement {
  const { showSuccess, showError } = useNotify();
  const [copiedAt, setCopiedAt] = useState<number | null>(null);

  useEffect(() => {
    if (copiedAt !== null) {
      const timeout = setTimeout(
        () => {
          setCopiedAt(null);
        },
        COPIED_DURATION - (Date.now() - copiedAt)
      );

      return () => {
        clearTimeout(timeout);
      };
    }

    return undefined;
  }, [copiedAt]);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(text);

      setCopiedAt(Date.now());
      showSuccess(`Copied "${text}" to clipboard.`, COPIED_DURATION);
    } catch {
      showError(`Copy failed.`);
    }
  }, [showSuccess, showError, text]);

  const copied = copiedAt !== null;
  const fullLabel = `Copy ${label ?? `text`}`;

  return (
    <Button
      variant="utilityIcon"
      sx={{ display: `inline-block` }}
      title={fullLabel}
      aria-label={fullLabel}
      onClick={handleCopy}
    >
      <Icon
        style={{ verticalAlign: `top` }}
        icon={copied ? `check` : `copy`}
        aria-hidden={true}
      />
    </Button>
  );
}
